import { createTheme } from '@mui/material/styles';
import { deriveTeamColors } from './utils/colorUtils';

// Default team colors
const DEFAULT_TEAM_COLORS = {
  team1: '#667eea',  // Blue
  team2: '#f5576c',  // Red/Pink
};

/**
 * Carga los colores de las parejas desde localStorage o usa los valores por defecto
 * @returns {object} Objeto con colores derivados para team1 y team2
 */
export const loadTeamColorsFromStorage = () => {
  const team1Color = localStorage.getItem('team1Color') || DEFAULT_TEAM_COLORS.team1;
  const team2Color = localStorage.getItem('team2Color') || DEFAULT_TEAM_COLORS.team2;

  return {
    team1: deriveTeamColors(team1Color),
    team2: deriveTeamColors(team2Color),
  };
};

// Team colors - used consistently across the app
// Exportamos como función para mantener compatibilidad con código existente
// pero permitiendo colores dinámicos
export const TEAM_COLORS = loadTeamColorsFromStorage();

// Global UI density for the MUI-driven screens (home, menus, online lobby/
// room, modals). These mostly use stock MUI sizing, which is tuned for
// desktop touch targets and reads as oversized on this app's compact,
// game-focused layouts. Trimming the spacing unit and baseline type scale
// here — plus a couple of components whose default padding is hardcoded in
// MUI rather than spacing-based — tightens things app-wide from one place.
// Does NOT affect the Ronda/Baciga PixiJS screens: those size themselves
// from their canvas container's pixel dimensions, not from this theme.
const SPACING_UNIT = 7; // MUI default is 8

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
      contrastText: '#fff',
    },
    secondary: {
      main: '#dc004e',
      light: '#e33371',
      dark: '#9a0036',
      contrastText: '#fff',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: [
      'Segoe UI',
      'Tahoma',
      'Geneva',
      'Verdana',
      'sans-serif',
    ].join(','),
    htmlFontSize: 16, // Base font size
    fontSize: 13, // Default body font size (MUI default is 14)
  },
  spacing: SPACING_UNIT,
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  shape: {
    borderRadius: 16,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '1rem',
          fontWeight: 600,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
        },
        outlined: {
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
          '&:hover': {
            background: 'rgba(255, 255, 255, 1)',
          },
        },
        // MUI's "large" size is used for several primary CTAs and toolbar
        // icons around the app; its default padding/font-size are hardcoded
        // (not spacing-based), so SPACING_UNIT alone doesn't shrink it.
        sizeLarge: {
          padding: '8px 20px',
          fontSize: '0.95rem',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        sizeLarge: {
          padding: 8,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: '1rem',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          transition: 'all 0.3s ease',
        },
      },
    },
    // CardContent/Container padding is hardcoded px in MUI's own styles
    // rather than spacing-based, so it needs explicit shrinking too.
    // (Dialog padding is deliberately left alone: several modals in this
    // app hand-position a floating close button against the default
    // DialogTitle padding, e.g. ChangelogModal.jsx, and shrinking it
    // globally made long titles wrap under that button.)
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 12,
          '&:last-child': { paddingBottom: 12 },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: 12,
          paddingRight: 12,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(20px)',
          color: '#1a1a1a',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          borderRadius: '1rem',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          zIndex: 1,
        },
      },
    },
  },
});

export default theme;
